<script lang="ts">
    
    import { onMount } from "svelte";
    import { Link } from "svelte-navigator";
    import { Badge, Button, Col, Icon, ModalBody, ModalHeader, Row, TabContent, Table, TabPane } from "sveltestrap";
    import { Modal } from "sveltestrap";
    import AccountName from "../../../Components/Accounts/AccountName.svelte";
    import AppWrapper from "../../../Components/AppWrapper.svelte";
    import NumEndedGames from "../../../Components/Manager/NumEndedGames.svelte";
    import DateFormatter from "../../../Components/Shared/DateFormatter.svelte";
    import TitleBar from "../../../Components/TitleBar.svelte";
    import UserForm from "../../../Components/Users/UserForm.svelte";
    import UserTypeLabel from "../../../Components/Users/UserTypeLabel.svelte";
    import Watch from "../../../Components/Watch.svelte";
    import { MANAGER_ID } from "../../../environment";
    import { Query } from "../../../Model/Query";
    import type { User } from "../../../Model/User";
    import { userFactory, usersService } from "../../../Services";

    const limit = 25;
    
    let isSearching = false;
    let openUserModal = false;
    let isNew = false;
    let editUser: User;
    let users: User[] = [];
    let userSearch = '';
    let offset = 0;
    let hasMore = false;

    onMount(getUsers);

    function onCreateUser(): void {
        isNew = true;
        editUser = userFactory.createUser();
        openUserModal = true;
    }

    function toggleUserModal(): void {
        openUserModal = !openUserModal;
    }

    function onEdit(user: User): void {
        editUser = user;
        isNew = false;
        openUserModal = true;
    }

    function onSaved(): void {
        if (isNew) {
            //El usuario en edición no está en la lista de usuarios actuales
            users = [...users, editUser];
        }
        editUser = undefined;
        openUserModal = false;
    }

    async function onRemove(): Promise<void> {
        if (!editUser || !confirm(`¿Seguro que desea eliminar el usuario "${editUser.name.value}".?`)) {
            return;
        }
        try {

            await usersService.deleteUser(editUser.id);

            users = users.filter(({ id }) => id !== editUser.id);
            editUser = undefined;
            openUserModal = false;

        } catch (error) {
            console.error(error);
        }
    }

    async function getUsers(): Promise<void> {
        if (isSearching) {
            return;
        }
        isSearching = true;
        
        const query = new Query(offset, limit);
        
        if (userSearch) {
            query.addFilter('search', [userSearch]);
        }

        try {
            const page = await usersService.queryUsers(query);
            users = [...users, ...page];
            hasMore = page.length === limit;
        } catch (error) {
            console.error(error);
        } finally {
            isSearching = false;
        }
    }

    function onSearch(): void {
        if (isSearching) {
            return;
        }
        offset = 0;
        users = [];
        getUsers();
    }

    function getMore(): void {
        if (isSearching) {
            return;
        }
        offset += limit;
        getUsers();
    }

</script>

<AppWrapper>
    
    <TitleBar title="Usuarios">
        <Button type="button" color="primary" on:click={onCreateUser}>
            <Icon name="person" /> Nuevo
        </Button>
    </TitleBar>


    <form on:submit|preventDefault={onSearch}>
        <Row>
            <Col xs={12} sm={10} md={6}>
                <div class="input-group mb-3">
                    <input type="search" class="form-control" bind:value={userSearch}>
                    <button type="submit" class="btn btn-primary" disabled={isSearching}>
                        {isSearching ? 'Buscando...' : 'Buscar'}
                    </button>
                </div>
            </Col>
        </Row>
    </form>

    <Table>
        <thead>
            <tr>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Estado</th>
                <th>Último acceso</th>
                <th>Creado</th>
                <th></th>
            </tr>
        </thead>
        <tbody>
            {#each users as user}
                <tr>
                    <td>
                        <Watch state={user.name} />
                    </td>
                    <td>
                        <Watch state={user.typeId} let:value={typeId}>
                            <UserTypeLabel {typeId} />
                            {#if typeId === MANAGER_ID}
                                <NumEndedGames managerId={user.id} let:count>
                                    <Badge color={count > 0 ? 'success' : 'warning'}>{count} juegos</Badge>
                                </NumEndedGames> 
                                (<Link to="/cuentas/editar/{user.accountId}"><AccountName accountId={user.accountId} /></Link>)
                            {/if}
                        </Watch>
                    </td>
                    <td>
                        <Watch state={user.enabled} let:value>
                            {#if value}
                                <Badge color="success">Activado</Badge>
                            {:else}
                                <Badge color="danger">Desactivado</Badge>
                            {/if}
                        </Watch>
                    </td>
                    <td>
                        <DateFormatter date={user.lastLoginDate} />
                    </td>
                    <td>
                        <DateFormatter date={user.creationDate} />
                    </td>
                    <td class="text-end">
                        <Button type="button" color="primary" size="sm" on:click={() => onEdit(user)}>
                            Editar
                        </Button>
                    </td>
                </tr>
            {/each}
        </tbody>
    </Table>

    {#if hasMore}
    <Row>
        <Col xs={12} class="d-flex justify-content-center">
            <button type="button" class="btn btn-primary" on:click={getMore}>Ver más</button>
        </Col>
    </Row>
    {/if}

</AppWrapper>

<Modal isOpen={openUserModal}>
    <ModalHeader toggle={toggleUserModal}>
        <Icon name="person" /> Editar usuario
    </ModalHeader>
    <ModalBody>
        <TabContent>
            <TabPane active tabId="edit" tab="Editar">
                <div class="pt-3">
                    <UserForm user={editUser} on:saved={onSaved} />
                </div>
            </TabPane>
            {#if !isNew}
            <TabPane tabId="del" tab="Eliminar">
                <div class="pt-3">
                    <Button type="button" color="danger" on:click={onRemove}>
                        <Icon name="x" /> Eliminar
                    </Button>
                </div>
            </TabPane>
            {/if}
        </TabContent>
    </ModalBody>
</Modal>
